import React from "react";
import * as Icon from "react-feather";

import "./statement-entry-modal.css";

import BookkeepingService from "../../../../services/BookkeepingService";
import UtilityService from "../../../../services/UtilityService";

import WComponent from "../../../common/WComponent";
import ToolsModal from "../kit/ToolsModal";
import ToolsSelect from "../kit/ToolsSelect";
import ToolsTieStrip from "../kit/ToolsTieStrip";

const STATEMENT_TYPES = [
	{ value: "bank", label: "Bank account" },
	{ value: "credit_card", label: "Credit card" },
];

let linesEverStarted = 0;

function anEmptyLine() {
	linesEverStarted += 1;
	return { id: linesEverStarted, date: "", description: "", amount: "" };
}

class StatementEntryModal extends WComponent {
	constructor(props) {
		super(props);

		const missing = props.theMonthThatIsMissing || {};

		this.state = {
			accountLabel: props.accountLabel || "",
			statementType: props.statementType || "bank",
			periodStart: missing.periodStart || "",
			periodEnd: missing.periodEnd || "",
			openingBalance: missing.openingBalance !== undefined && missing.openingBalance !== null ? String(missing.openingBalance) : "",
			closingBalance: missing.closingBalance !== undefined && missing.closingBalance !== null ? String(missing.closingBalance) : "",
			lines: [anEmptyLine()],
			linesCameFromAFile: false,
			linesWereChangedAfterwards: false,
			readingCsv: false,
			csvNote: "",
			error: "",
			saving: false,
		};
	}

	onLineChanged = ({ lineId, field, value }) => {
		const lines = this.state.lines.map(line => (line.id === lineId ? { ...line, [field]: value } : line));
		this.update({ lines, linesWereChangedAfterwards: true, error: "" });
	};

	onAddLine = () => this.update({ lines: [...this.state.lines, anEmptyLine()], linesWereChangedAfterwards: true });

	onRemoveLine = ({ lineId }) => {
		const lines = this.state.lines.filter(line => line.id !== lineId);
		this.update({ lines: lines.length > 0 ? lines : [anEmptyLine()], linesWereChangedAfterwards: true });
	};

	onCsvChosen = async event => {
		const file = (event.target.files || [])[0];
		event.target.value = "";
		if (!file) return;

		await this.update({ readingCsv: true, csvNote: "", error: "" });

		const read = await BookkeepingService.extractTransactionsFromStatementFile({ file, clientId: this.props.clientId });
		if (read.error) {
			await this.update({ readingCsv: false, error: read.error });
			return;
		}

		const lines = (read.transactions || []).map(transaction => ({
			...anEmptyLine(),
			date: transaction.date || "",
			description: transaction.description || "",
			amount: isNaN(transaction.amount) ? "" : String(transaction.amount),
		}));

		if (lines.length === 0) {
			await this.update({ readingCsv: false, error: "No lines could be read from that file." });
			return;
		}

		const couldNotBeRead = read.invalidCount
			? ` ${read.invalidCount} line${read.invalidCount === 1 ? "" : "s"} came through incomplete — check them before filing.`
			: "";

		await this.update({
			lines,
			linesCameFromAFile: true,
			linesWereChangedAfterwards: false,
			readingCsv: false,
			csvNote: `${lines.length} line${lines.length === 1 ? "" : "s"} read from ${file.name}.${couldNotBeRead}`,
		});
	};

	linesWorthFiling = () => this.state.lines.filter(line => line.date && line.amount !== "");

	describeTheTie = () => {
		const { openingBalance, closingBalance } = this.state;
		const opening = parseFloat(openingBalance);
		const closing = parseFloat(closingBalance);
		const lines = this.linesWorthFiling();

		const movement = lines.reduce((running, line) => running + (parseFloat(line.amount) || 0), 0);
		const bothEndsKnown = !isNaN(opening) && !isNaN(closing);
		const reached = isNaN(opening) ? null : Math.round((opening + movement) * 100) / 100;
		const difference = bothEndsKnown ? Math.round((reached - closing) * 100) / 100 : null;

		return {
			lineCount: lines.length,
			movement: Math.round(movement * 100) / 100,
			opening: isNaN(opening) ? null : opening,
			closing: isNaN(closing) ? null : closing,
			reached,
			difference,
			ties: difference !== null && Math.abs(difference) < 0.01,
			canBeJudged: bothEndsKnown && lines.length > 0,
		};
	};

	isReadyToFile = () => {
		const { accountLabel, periodStart, periodEnd, openingBalance, closingBalance } = this.state;
		const theStatementIsIdentified = !!(accountLabel && periodStart && periodEnd);
		const bothEndsAreGiven = openingBalance !== "" && closingBalance !== "";
		const thereIsSomethingOnIt = this.linesWorthFiling().length > 0;
		return theStatementIsIdentified && bothEndsAreGiven && thereIsSomethingOnIt;
	};

	onFile = async () => {
		const { clientId, enteredFrom, onFiled } = this.props;
		const { accountLabel, statementType, periodStart, periodEnd, openingBalance, closingBalance, linesCameFromAFile, linesWereChangedAfterwards } = this.state;

		await this.update({ saving: true, error: "" });

		const filed = await BookkeepingService.recordStatementByHand({
			clientId,
			accountLabel,
			statementType,
			periodStart,
			periodEnd,
			openingBalance: parseFloat(openingBalance),
			closingBalance: parseFloat(closingBalance),
			transactions: this.linesWorthFiling().map(line => ({
				date: line.date,
				description: line.description,
				amount: parseFloat(line.amount),
			})),
			enteredFrom: enteredFrom || BookkeepingService.describeHowAStatementWasEntered({ linesCameFromAFile, linesWereChangedAfterwards }),
		});

		if (filed.error) {
			await this.update({ saving: false, error: filed.error });
			return;
		}

		const tie = this.describeTheTie();

		await this.update({ saving: false });
		onFiled({
			statementId: filed.statementId,
			parse: {
				fileName: `${accountLabel} — ${periodStart} to ${periodEnd}`,
				fileHash: filed.fileHash,
				statementType,
				status: "ok",
				enteredByHand: true,
				bankName: null,
				accountNumberMasked: null,
				accountHolder: null,
				currency: null,
				periodStart,
				periodEnd,
				openingBalance: tie.opening,
				closingBalance: tie.closing,
				transactions: this.linesWorthFiling().map(line => ({
					date: line.date,
					description: line.description,
					amount: parseFloat(line.amount),
				})),
				integrity: { opening_to_closing_check: { ok: tie.ties, computed: tie.reached, stated: tie.closing, delta: tie.difference } },
				yearCorrections: [],
			},
		});
	};

	renderContext = () => {
		const { stalledFile, theMonthThatIsMissing } = this.props;

		if (theMonthThatIsMissing) {
			const gap = (theMonthThatIsMissing.amount < 0 ? "-$" : "$") + UtilityService.formatCurrency(Math.abs(theMonthThatIsMissing.amount));

			return (
				<div className="se-modal__context">
					<span className="tk-icon-chip se-modal__context-chip">
						<Icon.AlertTriangle size={15} />
					</span>
					<span className="se-modal__context-words">
						<span className="se-modal__context-name">The month nothing on file covers</span>
						<span className="se-modal__context-why">
							The period and both balances are already known from the statements either side. The lines below have to account for the {gap} the
							balance moves across the gap, which is what the check underneath is measuring.
						</span>
					</span>
				</div>
			);
		}

		if (!stalledFile) return null;

		return (
			<div className="se-modal__context">
				<span className="tk-icon-chip se-modal__context-chip">
					<Icon.FileText size={15} />
				</span>
				<span className="se-modal__context-words">
					<span className="se-modal__context-name">{stalledFile.fileName}</span>
					<span className="se-modal__context-why" title={stalledFile.whyItStalled}>{stalledFile.whyItStalled}</span>
				</span>
			</div>
		);
	};

	renderHeaderFields = () => {
		const { accountOptions = [] } = this.props;
		const { accountLabel, statementType, periodStart, periodEnd, openingBalance, closingBalance } = this.state;

		return (
			<div className="se-modal__fields">
				<div className="tk-form-field se-modal__field se-modal__field--account">
					<span className="tk-form-label">Account <span className="tk-form-required">*</span></span>
					{accountOptions.length > 0 ? (
						<ToolsSelect
							value={accountLabel}
							options={accountOptions}
							onChange={value => this.update({ accountLabel: value, error: "" })}
							placeholder="Which account is this?"
							searchable
						/>
					) : (
						<input
							type="text"
							className="tk-input"
							value={accountLabel}
							placeholder="Name the account this statement belongs to"
							onChange={event => this.update({ accountLabel: event.target.value, error: "" })}
						/>
					)}
				</div>
				<div className="tk-form-field se-modal__field">
					<span className="tk-form-label">Kind</span>
					<ToolsSelect value={statementType} options={STATEMENT_TYPES} onChange={value => this.update({ statementType: value })} />
				</div>
				<div className="tk-form-field se-modal__field">
					<span className="tk-form-label">Period start <span className="tk-form-required">*</span></span>
					<input type="date" className="tk-input" value={periodStart} onChange={event => this.update({ periodStart: event.target.value, error: "" })} />
				</div>
				<div className="tk-form-field se-modal__field">
					<span className="tk-form-label">Period end <span className="tk-form-required">*</span></span>
					<input type="date" className="tk-input" value={periodEnd} onChange={event => this.update({ periodEnd: event.target.value, error: "" })} />
				</div>
				<div className="tk-form-field se-modal__field">
					<span className="tk-form-label">Opening balance <span className="tk-form-required">*</span></span>
					<input
						type="number"
						step="0.01"
						className="tk-input se-modal__number"
						value={openingBalance}
						placeholder="0.00"
						onChange={event => this.update({ openingBalance: event.target.value, error: "" })}
					/>
				</div>
				<div className="tk-form-field se-modal__field">
					<span className="tk-form-label">Closing balance <span className="tk-form-required">*</span></span>
					<input
						type="number"
						step="0.01"
						className="tk-input se-modal__number"
						value={closingBalance}
						placeholder="0.00"
						onChange={event => this.update({ closingBalance: event.target.value, error: "" })}
					/>
				</div>
			</div>
		);
	};

	renderLines = () => {
		const { lines, readingCsv, csvNote } = this.state;

		return (
			<div className="se-modal__lines">
				<div className="se-modal__lines-head">
					<span className="tk-section-label">Lines</span>
					<label className="tk-text-action se-modal__csv">
						{readingCsv ? <Icon.Loader size={13} className="se-modal__spinner" /> : <Icon.Upload size={13} />}
						{readingCsv ? "Reading…" : "Read them from a CSV instead"}
						<input type="file" accept=".csv,.xlsx" className="se-modal__csv-input" onChange={this.onCsvChosen} />
					</label>
				</div>

				{csvNote && <p className="se-modal__csv-note">{csvNote}</p>}

				<div className="se-modal__grid">
					<div className="se-modal__row se-modal__row--headings">
						<span>Date</span>
						<span>What it says on the statement</span>
						<span className="se-modal__cell--right">Amount</span>
						<span />
					</div>
					<div className="se-modal__rows">
						{lines.map(line => (
							<div key={line.id} className="se-modal__row">
								<input
									type="date"
									className="tk-input se-modal__cell-input"
									value={line.date}
									onChange={event => this.onLineChanged({ lineId: line.id, field: "date", value: event.target.value })}
								/>
								<input
									type="text"
									className="tk-input se-modal__cell-input"
									value={line.description}
									placeholder="Description"
									onChange={event => this.onLineChanged({ lineId: line.id, field: "description", value: event.target.value })}
								/>
								<input
									type="number"
									step="0.01"
									className="tk-input se-modal__cell-input se-modal__number"
									value={line.amount}
									placeholder="0.00"
									onChange={event => this.onLineChanged({ lineId: line.id, field: "amount", value: event.target.value })}
								/>
								<button type="button" className="se-modal__drop" title="Remove this line" onClick={() => this.onRemoveLine({ lineId: line.id })}>
									<Icon.X size={13} />
								</button>
							</div>
						))}
					</div>
					<button type="button" className="se-modal__add" onClick={this.onAddLine}>
						<Icon.Plus size={13} />
						Add a line
					</button>
				</div>

				<p className="se-modal__sign-note">Money in is positive, money out is negative — the same way the statement moves the balance.</p>
			</div>
		);
	};

	renderTie = () => {
		const tie = this.describeTheTie();
		const money = value => UtilityService.formatSignedCurrency({ value });

		if (!tie.canBeJudged) {
			return (
				<ToolsTieStrip
					waiting
					waitingFor="Enter both balances and at least one line — the figures are checked against each other before anything is filed."
				/>
			);
		}

		return (
			<ToolsTieStrip
				ties={tie.ties}
				opening={tie.opening}
				lineCount={tie.lineCount}
				movement={tie.movement}
				reached={tie.reached}
				verdict={tie.ties ? "ties to the closing balance" : `${money(Math.abs(tie.difference))} away from the closing balance of ${money(tie.closing)}`}
			/>
		);
	};

	renderFooter = () => {
		const { onClose } = this.props;
		const { saving } = this.state;
		const tie = this.describeTheTie();
		const ready = this.isReadyToFile();

		return (
			<>
				<span className="se-modal__footer-note">
					{ready && !tie.ties
						? "This statement will be filed as not tying, so it stays visible as unresolved."
						: "Recorded as entered by hand, so a reviewer can see these figures were not read off a statement."}
				</span>
				<span className="tk-modal-actions">
					<button type="button" className="tk-button tk-button--secondary" onClick={onClose} disabled={saving}>
						Cancel
					</button>
					<button type="button" className="tk-button tk-button--primary" onClick={this.onFile} disabled={!ready || saving}>
						<Icon.Check size={16} />
						{saving ? "Filing…" : "File this statement"}
					</button>
				</span>
			</>
		);
	};

	render() {
		const { onClose } = this.props;
		const { error } = this.state;

		return (
			<ToolsModal open={true} onClose={onClose} title="Enter a statement by hand" width={860} footer={this.renderFooter()}>
				{this.renderContext()}
				{this.renderHeaderFields()}
				{this.renderLines()}
				{this.renderTie()}
				{error && (
					<div className="se-modal__error">
						<Icon.AlertCircle size={14} />
						{error}
					</div>
				)}
			</ToolsModal>
		);
	}
}

export default StatementEntryModal;
